import {
  buildMfaTwoFactorFromSession,
  checkRateLimit,
  clearMfaFailedAttempts,
  computeMfaLockedOut,
  createInitialTwoFactorState,
  logAuditEvent,
  mfaAfterPasswordSignIn,
  mfaDisable,
  mfaDisableSendChallenge,
  mfaDisableVerifyAndUnenroll,
  mfaEnrollTotp,
  mfaEnrollVerify,
  mfaGenerateRecoveryCodes,
  mfaVerifyLoginChallenge,
  mfaAfterRecoveryVerifySuccess,
  mfaVerifyRecoveryCode,
  MFA_MAX_FAILED_ATTEMPTS,
  recordMfaFailedAttempt,
  resetRateLimit,
  sanitizeEmail,
  saveLoginTimestamp,
  type MfaPendingSession,
  type MfaRateLimitState,
  type TwoFactorState,
} from "@dwelldrop/shared";
import { getSupabase, type Session } from "@dwelldrop/supabase";
import type { Dispatch, SetStateAction } from "react";
import {
  auditActionSource,
  checkVenueAccessForLogin,
  fetchProfile,
  getLockoutMessage,
  SUSPENDED_ERROR_MESSAGE,
  type AuthState,
  type PendingSession,
  type RateLimitState,
  type VenueProfile,
  VENUE_STORAGE_PREFIX,
} from "./context";
import { VENUE_MFA_CONFIG } from "./venueMfaConfig";

export type VenueMfaPendingSession = MfaPendingSession<VenueProfile>;

export type VenueSignInParams = {
  email: string;
  password: string;
  rememberMe: boolean;
  rateLimit: RateLimitState;
  setRateLimit: Dispatch<SetStateAction<RateLimitState>>;
  isLockedOut: () => boolean;
  setPendingSession: Dispatch<SetStateAction<PendingSession | null>>;
  setState: Dispatch<SetStateAction<AuthState>>;
  signInInProgress: { current: boolean };
};

export async function venueSignInWithPassword(
  params: VenueSignInParams
): Promise<{
  error: string | null;
  needs2FA: boolean;
  retryAfterSeconds?: number;
}> {
  const {
    email,
    password,
    rememberMe,
    rateLimit,
    setRateLimit,
    isLockedOut,
    setPendingSession,
    setState,
    signInInProgress,
  } = params;

  if (isLockedOut()) {
    const remainingSec = rateLimit.lockedUntil
      ? Math.max(0, Math.ceil((rateLimit.lockedUntil - Date.now()) / 1000))
      : 900;
    return {
      error: getLockoutMessage(rateLimit.lockedUntil),
      needs2FA: false,
      retryAfterSeconds: remainingSec,
    };
  }

  const supabase = getSupabase(VENUE_MFA_CONFIG.tokenStorageKey);
  const cleanEmail = sanitizeEmail(email);

  signInInProgress.current = true;

  try {
    const rl = await checkRateLimit(
      supabase,
      VENUE_MFA_CONFIG.loginRateKey(cleanEmail),
      "login"
    );
    if (!rl.allowed) {
      const retrySeconds = rl.retryAfterSeconds ?? 900;
      const retryMin = Math.max(1, Math.ceil(retrySeconds / 60));
      setRateLimit((r) => ({
        ...r,
        failedAttempts: MFA_MAX_FAILED_ATTEMPTS,
        lockedUntil: Date.now() + retrySeconds * 1000,
      }));
      await logAuditEvent(
        supabase,
        "rate_limit_exceeded",
        null,
        { email: cleanEmail, app: "venue" },
        "auth",
        auditActionSource()
      );
      return {
        error: `Too many login attempts. Please try again in ${retryMin} minute${retryMin > 1 ? "s" : ""}.`,
        needs2FA: false,
        retryAfterSeconds: retrySeconds,
      };
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email: cleanEmail,
      password,
    });

    if (error) {
      recordMfaFailedAttempt(setRateLimit);
      await logAuditEvent(
        supabase,
        "login_failure",
        null,
        { email: cleanEmail, reason: error.message, app: "venue" },
        "auth",
        auditActionSource()
      );
      const userMessage =
        (error as { code?: string }).code === "invalid_credentials"
          ? "Invalid credentials"
          : (error as { code?: string }).code === "user_banned" ||
              error.message === SUSPENDED_ERROR_MESSAGE
            ? SUSPENDED_ERROR_MESSAGE
            : error.message;
      return { error: userMessage, needs2FA: false };
    }

    if (!data.session || !data.user) {
      return { error: "Login failed. Please try again.", needs2FA: false };
    }

    const profile = await fetchProfile(
      data.user.id,
      2,
      1000,
      data.session.access_token
    );
    if (!profile) {
      await supabase.auth.signOut();
      return {
        error:
          "Access denied. This portal is for venue owners, staff, and property operators.",
        needs2FA: false,
      };
    }

    if (profile.role === "venue_staff" || profile.role === "venue_owner") {
      const venueActive = await checkVenueAccessForLogin(
        profile.role,
        data.user.id,
        data.session.access_token
      );
      if (!venueActive) {
        await logAuditEvent(
          supabase,
          "login_blocked_venue_suspended",
          data.user.id,
          { app: "venue", role: profile.role },
          "auth",
          auditActionSource()
        );
        await supabase.auth.signOut();
        return { error: SUSPENDED_ERROR_MESSAGE, needs2FA: false };
      }
    }

    clearMfaFailedAttempts(setRateLimit);

    const setTwoFactor = (action: SetStateAction<TwoFactorState>) => {
      setState((s) => ({
        ...s,
        twoFactor:
          typeof action === "function" ? action(s.twoFactor) : action,
      }));
    };

    const mfaResult = await mfaAfterPasswordSignIn({
      config: VENUE_MFA_CONFIG,
      userId: data.user.id,
      profile,
      rateLimit: rateLimit as MfaRateLimitState,
      setRateLimit: setRateLimit as Dispatch<SetStateAction<MfaRateLimitState>>,
      setPendingSession: setPendingSession as Dispatch<
        SetStateAction<VenueMfaPendingSession | null>
      >,
      setTwoFactor,
      rememberMe,
      user: data.user,
      session: data.session,
    });

    if (mfaResult.needsMfaChallenge) {
      return { error: null, needs2FA: true };
    }

    await resetRateLimit(supabase, VENUE_MFA_CONFIG.loginRateKey(cleanEmail));
    await logAuditEvent(
      supabase,
      "login_success",
      data.user.id,
      { app: "venue", rememberMe },
      "auth",
      auditActionSource()
    );

    saveLoginTimestamp(VENUE_STORAGE_PREFIX, rememberMe);

    const twoFactorBits = await buildMfaTwoFactorFromSession(
      VENUE_MFA_CONFIG.tokenStorageKey,
      profile.phone
    );

    setState({
      user: data.user,
      session: data.session,
      loading: false,
      profile,
      sessionExpired: false,
      twoFactor: {
        ...createInitialTwoFactorState(),
        ...twoFactorBits,
      },
    });

    return { error: null, needs2FA: false };
  } finally {
    signInInProgress.current = false;
  }
}

export function createVenueMfaHandlers(deps: {
  pendingSession: PendingSession | null;
  rateLimit: RateLimitState;
  setRateLimit: Dispatch<SetStateAction<RateLimitState>>;
  setPendingSession: Dispatch<SetStateAction<PendingSession | null>>;
  setState: Dispatch<SetStateAction<AuthState>>;
  isLockedOut: () => boolean;
  twoFactor: TwoFactorState;
  userId: string | null;
  mfaCompletingRef?: { current: boolean };
  pendingMfaVerificationRef?: { current: boolean };
}) {
  const {
    pendingSession,
    rateLimit,
    setRateLimit,
    setPendingSession,
    setState,
    isLockedOut,
    twoFactor,
    userId,
    mfaCompletingRef,
    pendingMfaVerificationRef,
  } = deps;

  const setTwoFactor = (action: SetStateAction<TwoFactorState>) => {
    setState((s) => ({
      ...s,
      twoFactor:
        typeof action === "function" ? action(s.twoFactor) : action,
    }));
  };

  const onLoginSuccess = async (
    pending: VenueMfaPendingSession,
    session: Session
  ) => {
    if (mfaCompletingRef) mfaCompletingRef.current = true;
    if (pendingMfaVerificationRef) pendingMfaVerificationRef.current = false;
    try {
      saveLoginTimestamp(VENUE_STORAGE_PREFIX, pending.rememberMe);
      const twoFactorBits = await buildMfaTwoFactorFromSession(
        VENUE_MFA_CONFIG.tokenStorageKey,
        pending.profile.phone
      );
      setState({
        user: session.user,
        session,
        loading: false,
        profile: pending.profile,
        sessionExpired: false,
        twoFactor: {
          ...createInitialTwoFactorState(),
          ...twoFactorBits,
        },
      });
    } finally {
      if (mfaCompletingRef) mfaCompletingRef.current = false;
    }
  };

  return {
    verify2FA: (code: string) => {
      const factorId = twoFactor.mfaFactorId;
      const challengeId = twoFactor.mfaChallengeId;
      if (!factorId || !challengeId) {
        return Promise.resolve({
          error: "MFA challenge expired. Please sign in again.",
        });
      }
      return mfaVerifyLoginChallenge({
        config: VENUE_MFA_CONFIG,
        code,
        factorId,
        challengeId,
        pendingSession,
        isLockedOut,
        lockedUntil: rateLimit.lockedUntil,
        setRateLimit: setRateLimit as Dispatch<
          SetStateAction<MfaRateLimitState>
        >,
        setPendingSession: setPendingSession as Dispatch<
          SetStateAction<VenueMfaPendingSession | null>
        >,
        setTwoFactor,
        onLoginSuccess,
      });
    },
    enable2FAEnrollTotp: () => mfaEnrollTotp({ config: VENUE_MFA_CONFIG }),
    enable2FAVerify: (params: {
      code: string;
      factorId: string;
      challengeId?: string;
      phone?: string;
    }) =>
      mfaEnrollVerify({
        config: VENUE_MFA_CONFIG,
        code: params.code,
        factorId: params.factorId,
        challengeId: params.challengeId,
        phone: params.phone,
        userId,
        setTwoFactor,
      }),
    disable2FA: () => mfaDisable(VENUE_MFA_CONFIG, userId, setTwoFactor),
    disable2FASendChallenge: () =>
      mfaDisableSendChallenge({
        config: VENUE_MFA_CONFIG,
        userId,
        rateLimit: rateLimit as MfaRateLimitState,
        setRateLimit: setRateLimit as Dispatch<
          SetStateAction<MfaRateLimitState>
        >,
        setTwoFactor,
        isLockedOut,
        lockedUntil: rateLimit.lockedUntil,
      }),
    disable2FAVerify: (params: {
      code: string;
      factorId: string;
      challengeId: string;
    }) => {
      const { code, factorId, challengeId } = params;
      if (!factorId || !challengeId) {
        return Promise.resolve({
          error: "Verification expired. Request a new code and try again.",
        });
      }
      return mfaDisableVerifyAndUnenroll({
        config: VENUE_MFA_CONFIG,
        code,
        factorId,
        challengeId,
        userId,
        setTwoFactor,
        isLockedOut,
        lockedUntil: rateLimit.lockedUntil,
        setRateLimit: setRateLimit as Dispatch<
          SetStateAction<MfaRateLimitState>
        >,
      });
    },
    generateRecoveryCodes: () => mfaGenerateRecoveryCodes(VENUE_MFA_CONFIG),
    verifyRecoveryCode: async (code: string) => {
      const result = await mfaVerifyRecoveryCode(VENUE_MFA_CONFIG, code);
      if (result.error) return result;
      await mfaAfterRecoveryVerifySuccess({
        config: VENUE_MFA_CONFIG,
        setTwoFactor,
        clearPendingSession: () => setPendingSession(null),
      });
      return { error: null };
    },
  };
}

export { computeMfaLockedOut as venueComputeMfaLockedOut };
